import { motion } from "framer-motion";
import { debounce } from "lodash";
import {
  type ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { BotContext } from "@/pages/cbb-builder/store";
import { axiosGet, axiosPost } from "@/services/axiosService";
import { ADD_TEXT_WORD_LIMIT, getWords, limitWords } from "./addText.utils";
import Skeleton from "react-loading-skeleton";

const AddTextStep = () => {
  const [store, storeDispatch] = useContext(BotContext);
  const [isTraining, setIsTraining] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const textContentId = store?.botData?.aiSource?.textArea?.contentId ?? "";
  const botCustomId = store?.botData?.botCustomId;
  const textValue = store.textAreaContent ?? "";
  const hasUnsavedChanges = textValue !== (store.textAreaContentPrev ?? "");
  const wordCount = getWords(textValue).length;
  const canTrain = wordCount > 0 && !isTraining && hasUnsavedChanges;
  const [isLoading, setIsLoading] = useState(false);
  const isTextareaActive = isFocused || textValue.trim().length > 0;

  useEffect(() => {
    async function fetchTextArea() {
      try {
        if (store.textAreaContent !== null || !textContentId) return;
        setIsLoading(true);
        const response = await axiosGet(`/ai/content/${textContentId}`);
        storeDispatch({
          type: "set-textAreaContent",
          textAreaContent:
            response.data.data.draftContent ?? response.data.data.content,
        });
        storeDispatch({
          type: "set-textAreaContentPrev",
          textAreaContent: response.data.data?.content,
        });
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTextArea();
  }, [store.textAreaContent, textContentId, storeDispatch]);

  const updateTextAreaSource = useCallback(
    async (contentId?: string) => {
      const updatedBotData = {
        ...store.botData,
        aiSource: {
          ...(store.botData?.aiSource ?? {}),
          textArea: {
            ...(store.botData?.aiSource?.textArea ?? {}),
            ...(contentId ? { contentId } : {}),
            characterCount: textValue.length,
            status: "Training Complete",
          },
        },
      };

      storeDispatch({
        type: "set-botData",
        botData: updatedBotData,
      });

      try {
        await axiosPost("/users/saveBot", updatedBotData);
      } catch (err) {
        console.error("Failed to save bot", err);
      }
    },
    [store.botData, storeDispatch, textValue.length],
  );

  const autoSave = useMemo(
    () =>
      debounce(async (content: string) => {
        try {
          storeDispatch({
            type: "set-textAreaStatus",
            textAreaStatus: true,
          });

          const response = await axiosPost("/ai/content", {
            id: textContentId,
            draftContent: content,
            botCustomId,
          });

          const contentId = response?.data?.data?._id;

          storeDispatch({
            type: "set-textAreaContent",
            textAreaContent: content,
          });

          if (contentId && !textContentId) {
            const updatedBotData = {
              ...store.botData,
              aiSource: {
                ...(store.botData?.aiSource ?? {}),
                textArea: {
                  ...(store.botData?.aiSource?.textArea ?? {}),
                  contentId,
                },
              },
            };
            storeDispatch({
              type: "set-botData",
              botData: updatedBotData,
            });
            try {
              await axiosPost("/users/saveBot", updatedBotData);
            } catch (err) {
              console.error("Failed to save bot", err);
            }
          }
        } catch (error) {
          console.log(error);
        } finally {
          storeDispatch({
            type: "set-textAreaStatus",
            textAreaStatus: false,
          });
        }
      }, 800),
    [botCustomId, store.botData, storeDispatch, textContentId],
  );

  useEffect(() => {
    return () => {
      autoSave.cancel();
    };
  }, [autoSave]);

  const handleTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = limitWords(event.target.value);

    storeDispatch({
      type: "set-textAreaContent",
      textAreaContent: nextValue,
    });
    autoSave(nextValue);
  };

  const handleTrainBot = async () => {
    if (!canTrain) return;

    try {
      setIsTraining(true);
      autoSave.cancel();

      const response = await axiosPost("/ai/content", {
        id: textContentId,
        content: textValue,
        draftContent: textValue,
        botCustomId,
      });

      updateTextAreaSource(response?.data?.data?._id);
      storeDispatch({
        type: "set-textAreaContentPrev",
        textAreaContent: textValue,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsTraining(false);
    }
  };

  return (
    <div className="p-[4px]">
      <motion.div
        className={`relative min-h-[345px] overflow-hidden rounded-[22px] border bg-[#fafafa] transition-colors ${
          isTextareaActive
            ? "border-[#64A8F2] shadow-[0_0_0_4px_rgba(42,130,211,0.10)]"
            : "border-[rgba(0,0,0,0.08)]"
        }`}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
      >
        {isLoading ? (
          <div className="px-[22px] py-[22px]">
            <Skeleton
              height={30}
              count={5}
              baseColor="#8c8c8c2b"
              highlightColor="#ffffff"
              duration={2}
            />
          </div>
        ) : (
          <textarea
            className="new-scrollbar h-[345px] w-full resize-none !border-b-0 bg-transparent px-[22px] py-[22px] text-[15px] font-normal leading-[150%] text-[#444] outline-none placeholder:text-[14px] placeholder:text-[#a8a8a8]"
            placeholder="Paste your text here..."
            value={textValue}
            onChange={handleTextAreaChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        )}

        <div className="flex items-center justify-end gap-[14px] px-[16Px] pb-[16px]">
          <span className="text-[12px] font-normal leading-[120%] text-[#828282]">
            {wordCount}/{ADD_TEXT_WORD_LIMIT} words
          </span>
          <span className="h-[25px] w-[1px] bg-[#0000004D]" />
          <motion.button
            className={`h-[33px] rounded-[8px] px-[15px] text-[12px] font-medium leading-[120%] text-white transition-colors ${
              canTrain
                ? "bg-[#FB5F66] hover:shadow-[0px_4px_20px_rgba(251,95,102,0.15)]"
                : "pointer-events-none bg-[#FB9AA0]"
            }`}
            type="button"
            whileTap={canTrain ? { scale: 0.97 } : undefined}
            onClick={handleTrainBot}
          >
            {isTraining
              ? "Training..."
              : !hasUnsavedChanges && wordCount > 0
                ? "Trained"
                : "Train Bot"}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default AddTextStep;
